import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Patient, PatientFormsContext, PatientFormsEvent } from '../state-machine/patient-forms.types';

export interface SavePatientResult {
	success: boolean;
	savedIteroPatient?: { uid: string; firstName: string; lastName: string; dateOfBirth: string; gender?: number; chartNumber: string };
	linkedPatient?: { iteroPatientId: string; invisalignPatientId: string };
}

/** Data shape the host app provides as fallback when the API has no result. */
export interface RxPatientData {
	UID: string;
	FirstName: string;
	LastName: string;
	DateOfBirth: Date | string;
	Gender?: number;
	ChartNumber: string;
}

type SendBack = (event: PatientFormsEvent) => void;

/**
 * Abstract service class for patient forms machine services.
 * Provide a concrete implementation via DI (stub for dev, real API for rx-app).
 */
@Injectable()
export abstract class PatientFormsServicesToken {
	abstract getRecentPatients(): Promise<Patient[]>;

	/**
	 * Persists the patient (save iTero, create link, or both) based on current machine state.
	 * Returns the result with optional data for the host app to update its store.
	 */
	abstract savePatient(context: PatientFormsContext, stateMatches: (path: string) => boolean): Observable<SavePatientResult>;

	/**
	 * Background search for patients matching form data as the user types on PatientCard.
	 * Invoked on patientCard.iTeroPatient.creating entry (re-invoked on each CARD.FORM_UPDATED).
	 * Searches across both systems. Results populate context.matchingPatients for linking suggestions.
	 * Skips if firstName/lastName are missing.
	 */
	abstract searchForMatchingPatients(context: PatientFormsContext, event: PatientFormsEvent): (sendBack: SendBack) => void;

	/**
	 * Checks if the pending patient exists in the other system (cross-system match).
	 * Invoked on patientMatch.loading entry after a patient is selected from search.
	 * Sends PATIENT_MATCH.MATCHES_FOUND, PATIENT_MATCH.NO_MATCHES, or PATIENT_MATCH.ERROR.
	 */
	abstract checkCrossSystemMatches(context: PatientFormsContext, event: PatientFormsEvent): (sendBack: SendBack) => void;

	/**
	 * Searches the full patient database by query string and system filter.
	 * Invoked on patientSearch.searchingDB entry when user types 3+ characters.
	 * Sends SEARCH.RESULTS with the found patients.
	 */
	abstract searchPatientsInDB(context: PatientFormsContext, event: PatientFormsEvent): (sendBack: SendBack) => void;

	/**
	 * Checks if an iTero patient with identical data already exists (duplicate prevention).
	 * Invoked on patientEditor.fillingInvisalignFields entry — specifically when creating
	 * a new iTero patient after choosing "proceed without linking".
	 * Unlike searchForMatchingPatients (broad, cross-system), this is a strict exact-match
	 * check within iTero only, used to block accidental duplicate creation.
	 */
	abstract checkDuplicateIteroPatient(context: PatientFormsContext, event: PatientFormsEvent): (sendBack: SendBack) => void;

	/**
	 * Fetches an existing patient by GUID from the API and maps to the Patient domain type.
	 * Falls back to fallbackPatient$ if the API fails or returns no result.
	 */
	abstract loadExistingPatient(patientGuid: string, fallbackPatient$: Observable<RxPatientData | null>): Observable<Patient | null>;
}
